import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Button,
  Text,
  Box,
  RadioGroup,
  Radio,
  useToast,
  FormHelperText,
  Spinner,
} from '@chakra-ui/react';
import ApplicationFormPart2 from './ApplicationFormPart2';
import { jobApplicationService } from '../../../services/jobApplicationService';

const ApplicationForm = ({ isOpen, onClose, jobTitle, jobCircularId, jobInfo }) => {
  const toast = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [duplicateStatus, setDuplicateStatus] = useState({
    email: { isDuplicate: false, isChecking: false, message: '' },
    mobile: { isDuplicate: false, isChecking: false, message: '' }
  });
  const [formData, setFormData] = useState({
    cv: null,
    name: '',
    nid: '',
    dateOfBirth: '',
    permanentAddress: '',
    presentAddress: '',
    bloodGroup: '',
    gender: '',
    mobileNo: '',
    email: '',
    latestEducationalQualification: '',
    recruitmentSource: '',
    careerObjective: '',
    achievement: '',
    messageToRecruiter: '',
    // Internship-specific fields
    instituteName: '',
    lastCGPA: '',
    areaOfInterest: [],
    // Part 2 fields
    knowledgeAboutCompany: '',
    passion: '',
    topSkills: '',
    biggestChallenge: '',
    taskCompletionRate: '',
    hardWorking: '',
    punctuality: '',
    problemSolving: '',
    intellectualCuriosity: '',
    criticizeFreely: '',
    communicationSkills: '',
    chatgptProficiency: '',
    businessAcumen: '',
    jobSuitability: '',
    workDuration: '',
    interestReason: ''
  });

  // Debounced duplicate check functions
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
  const debounceDelay = 1000; // 1 second delay
  const [emailTimeout, setEmailTimeout] = useState(null);
  const [mobileTimeout, setMobileTimeout] = useState(null);

  const checkEmailDuplicate = useCallback(async (email) => {
    if (!email || !emailRegex.test(email) || !jobCircularId) return;
    
    setDuplicateStatus(prev => ({
      ...prev,
      email: { ...prev.email, isChecking: true }
    }));

    try {
      const result = await jobApplicationService.checkDuplicate({
        email,
        jobCircularId,
      });

      setDuplicateStatus(prev => ({
        ...prev,
        email: {
          isDuplicate: result.duplicate,
          isChecking: false,
          message: result.duplicate ? 'This email has already been used for this job application' : ''
        }
      }));
    } catch (error) {
      setDuplicateStatus(prev => ({
        ...prev,
        email: { isDuplicate: false, isChecking: false, message: '' }
      }));
    }
  }, [jobCircularId]);

  const checkMobileDuplicate = useCallback(async (mobile) => {
    if (!mobile || !jobCircularId) return;
    
    setDuplicateStatus(prev => ({
      ...prev,
      mobile: { ...prev.mobile, isChecking: true }
    }));

    try {
      const result = await jobApplicationService.checkDuplicate({
        mobile,
        jobCircularId,
      });

      setDuplicateStatus(prev => ({
        ...prev,
        mobile: {
          isDuplicate: result.duplicate,
          isChecking: false,
          message: result.duplicate ? 'This mobile number has already been used for this job application' : ''
        }
      }));
    } catch (error) {
      setDuplicateStatus(prev => ({
        ...prev,
        mobile: { isDuplicate: false, isChecking: false, message: '' }
      }));
    }
  }, [jobCircularId]);

  // Email validation effect
  useEffect(() => {
    if (emailTimeout) clearTimeout(emailTimeout);
    
    if (formData.email && emailRegex.test(formData.email)) {
      const timeout = setTimeout(() => {
        checkEmailDuplicate(formData.email);
      }, debounceDelay);
      setEmailTimeout(timeout);
    } else {
      setDuplicateStatus(prev => ({
        ...prev,
        email: { isDuplicate: false, isChecking: false, message: '' }
      }));
    }

    return () => {
      if (emailTimeout) clearTimeout(emailTimeout);
    };
  }, [formData.email, checkEmailDuplicate]);

  // Mobile validation effect
  useEffect(() => {
    if (mobileTimeout) clearTimeout(mobileTimeout);
    
    if (formData.mobileNo) {
      const timeout = setTimeout(() => {
        checkMobileDuplicate(formData.mobileNo);
      }, debounceDelay);
      setMobileTimeout(timeout);
    } else {
      setDuplicateStatus(prev => ({
        ...prev,
        mobile: { isDuplicate: false, isChecking: false, message: '' }
      }));
    }

    return () => {
      if (mobileTimeout) clearTimeout(mobileTimeout);
    };
  }, [formData.mobileNo, checkMobileDuplicate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf' || file.size > 1024 * 1024) { // 1MB limit
        toast({
          title: "File Error",
          description: "Please upload a PDF file less than 1MB",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      handleInputChange('cv', file);
    }
  };

  // Validation helpers
  const validatePart1 = () => {
    const errors = [];
    const labels = {
      cv: 'CV',
      name: 'Name',
      nid: 'NID',
      dateOfBirth: 'Date of Birth',
      permanentAddress: 'Permanent Address',
      presentAddress: 'Present Address',
      bloodGroup: 'Blood Group',
      gender: 'Gender',
      mobileNo: 'Mobile No',
      email: 'Email',
      latestEducationalQualification: 'Latest Educational Qualification',
      recruitmentSource: 'Recruitment Source',
      careerObjective: 'Career Objective',
      achievement: 'Achievement'
    };

    Object.keys(labels).forEach(key => {
      if (!formData[key]) {
        errors.push(`${labels[key]} is required`);
      }
    });
    // NID rules: digits only & length 10 or 13 or 17
    if (formData.nid) {
      const nidDigitsOnly = /^\d+$/;
      const len = formData.nid.length;
      if (!nidDigitsOnly.test(formData.nid) || ![10,13,17].includes(len)) {
        errors.push('NID must be numeric and exactly 10, 13, or 17 digits long');
      }
    }
    // Email format
    if (formData.email && !emailRegex.test(formData.email)) {
      errors.push('Email format is invalid');
    }
    // Age validation - must be at least 18 years old
    if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();
      
      // Adjust age if birthday hasn't occurred this year yet
      const actualAge = (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) ? age - 1 : age;
      
      if (actualAge < 18) {
        errors.push('You must be at least 18 years old to apply for this position');
      }
    }
    // Check for duplicates
    if (duplicateStatus.email.isDuplicate) {
      errors.push('This email has already been used for this job application');
    }
    if (duplicateStatus.mobile.isDuplicate) {
      errors.push('This mobile number has already been used for this job application');
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if still checking for duplicates
    if (duplicateStatus.email.isChecking || duplicateStatus.mobile.isChecking) {
      toast({
        title: 'Please Wait',
        description: 'Checking for duplicate applications...',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const errors = validatePart1();
    if (errors.length) {
      toast({
        title: 'Validation Error',
        description: errors[0] + (errors.length > 1 ? ` (+${errors.length - 1} more)` : ''),
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setCurrentStep(2);
  };

  const handleFinalSubmit = () => {
    // Here you would typically send the complete form data to your API
    // Reset form and close modal
    setCurrentStep(1);
    setFormData({
      cv: null,
      name: '',
      nid: '',
      dateOfBirth: '',
      permanentAddress: '',
      presentAddress: '',
      bloodGroup: '',
      gender: '',
      mobileNo: '',
      email: '',
      latestEducationalQualification: '',
      recruitmentSource: '',
      careerObjective: '',
      achievement: '',
      messageToRecruiter: '',
      instituteName: '',
      lastCGPA: '',
      areaOfInterest: [],
      knowledgeAboutCompany: '',
      passion: '',
      topSkills: '',
      biggestChallenge: '',
      taskCompletionRate: '',
      hardWorking: '',
      punctuality: '',
      problemSolving: '',
      intellectualCuriosity: '',
      criticizeFreely: '',
      communicationSkills: '',
      chatgptProficiency: '',
      businessAcumen: '',
      jobSuitability: '',
      workDuration: '',
      interestReason: ''
    });
    onClose();
  };

  const handlePrevious = () => {
    setCurrentStep(1);
  };

  const handleClose = () => {
    setCurrentStep(1);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh" bg="white">
        <ModalCloseButton />
        <ModalBody p={8}>
          {currentStep === 1 ? (
            <VStack spacing={6} align="stretch">
              {/* Header */}
              <Box textAlign="center" mb={4}>
                <Text fontSize="2xl" fontWeight="bold" color="#2D3748" mb={2}>
                  Application Form: Part 1
                </Text>
                <Text color="gray.600" fontSize="sm" lineHeight="1.6">
                  Business Automation is an equal employment opportunity company that encourages employees to explore new
                  ideas and enable fresh talents to improve the operation. To assess your potential for our job, we need
                  below information. Please provide the information and apply.
                  <br />
                  (It will take approximately 20-25 minutes to fill out the form)
                </Text>
              </Box>

              <form onSubmit={handleSubmit}>
              <VStack spacing={4} align="stretch">
                {/* CV Upload */}
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontSize="sm">Attach Your CV</FormLabel>
                  <Input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    py={2}
                    border="1px solid"
                    borderColor="gray.300"
                  />
                  <Text fontSize="xs" color="red.500" mt={1}>
                    (File must be .pdf format and size less than 1 MB)
                  </Text>
                </FormControl>

                {/* Name */}
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontSize="sm">Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Write your name here"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                  />
                </FormControl>

                {/* NID */}
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontSize="sm">NID</FormLabel>
                  <Input
                    value={formData.nid}
                    onChange={(e) => handleInputChange('nid', e.target.value)}
                    placeholder="NID must be 10/13 or 17 digit long"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                  />
                </FormControl>

                {/* Date of Birth */}
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontSize="sm">Date of Birth</FormLabel>
                  <Input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                  />
                </FormControl>

                {/* Permanent Address */}
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontSize="sm">Permanent address</FormLabel>
                  <Input
                    value={formData.permanentAddress}
                    onChange={(e) => handleInputChange('permanentAddress', e.target.value)}
                    placeholder="Please add here"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                  />
                </FormControl>

                {/* Present Address */}
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontSize="sm">Present address</FormLabel>
                  <Input
                    value={formData.presentAddress}
                    onChange={(e) => handleInputChange('presentAddress', e.target.value)}
                    placeholder="Please add here"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                  />
                </FormControl>

                {/* Blood Group */}
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontSize="sm">Blood group</FormLabel>
                  <Select
                    value={formData.bloodGroup}
                    onChange={(e) => handleInputChange('bloodGroup', e.target.value)}
                    placeholder="Select"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                  >
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </Select>
                </FormControl>

                {/* Gender */}
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontSize="sm">Gender</FormLabel>
                  <RadioGroup
                    value={formData.gender}
                    onChange={(value) => handleInputChange('gender', value)}
                  >
                    <HStack spacing={6}>
                      <Radio value="Male">Male</Radio>
                      <Radio value="Female">Female</Radio>
                    </HStack>
                  </RadioGroup>
                </FormControl>

                {/* Mobile No */}
                <FormControl isRequired isInvalid={duplicateStatus.mobile.isDuplicate}>
                  <FormLabel color="gray.700" fontSize="sm">Mobile No</FormLabel>
                  <Input
                    value={formData.mobileNo}
                    onChange={(e) => handleInputChange('mobileNo', e.target.value)}
                    placeholder="Phone Number"
                    bg="white"
                    border="1px solid"
                    borderColor={duplicateStatus.mobile.isDuplicate ? "red.300" : "gray.300"}
                  />
                  {duplicateStatus.mobile.isChecking && (
                    <FormHelperText color="#000B8C">
                      <HStack spacing={2}>
                        <Spinner size="xs" />
                        <Text fontSize="xs">Checking for duplicate...</Text>
                      </HStack>
                    </FormHelperText>
                  )}
                  {duplicateStatus.mobile.message && (
                    <FormHelperText color="red.500" fontSize="xs">
                      {duplicateStatus.mobile.message}
                    </FormHelperText>
                  )}
                </FormControl>

                {/* Email */}
                <FormControl isRequired isInvalid={duplicateStatus.email.isDuplicate}>
                  <FormLabel color="gray.700" fontSize="sm">Email</FormLabel>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Your valid email"
                    bg="white"
                    border="1px solid"
                    borderColor={duplicateStatus.email.isDuplicate ? "red.300" : "gray.300"}
                  />
                  {duplicateStatus.email.isChecking && (
                    <FormHelperText color="#000B8C">
                      <HStack spacing={2}>
                        <Spinner size="xs" />
                        <Text fontSize="xs">Checking for duplicate...</Text>
                      </HStack>
                    </FormHelperText>
                  )}
                  {duplicateStatus.email.message && (
                    <FormHelperText color="red.500" fontSize="xs">
                      {duplicateStatus.email.message}
                    </FormHelperText>
                  )}
                </FormControl>

                {/* Latest Educational Qualification */}
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontSize="sm">Latest Educational Qualification</FormLabel>
                  <Select
                    value={formData.latestEducationalQualification}
                    onChange={(e) => handleInputChange('latestEducationalQualification', e.target.value)}
                    placeholder="--Select--"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                  >
                    <option value="Bachelor's Degree">Bsc in CSE</option>
                    <option value="Master's Degree">Bsc in EEE</option>
                  </Select>
                </FormControl>

                {/* Recruitment Source */}
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontSize="sm">Recruitment Source</FormLabel>
                  <Select
                    value={formData.recruitmentSource}
                    onChange={(e) => handleInputChange('recruitmentSource', e.target.value)}
                    placeholder="--Select--"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                  >
                    <option value="Facebook">Facebook</option>
                    <option value="Linkdin">Linkdin</option>
                    <option value="Twitter">Twitter</option>
                    <option value="Bd Jobs">Bd Jobs</option>
                    <option value="Friends">Friends</option>
                    <option value="Friends">ba Website</option>
                    <option value="Friends">Campaign</option>
                    <option value="Friends">Others</option>
                    <option value="Friends">University Alumni</option>
                    <option value="Friends">Google</option>
                  </Select>
                </FormControl>

                {/* Career Objective */}
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontSize="sm">Career Objective</FormLabel>
                  <Textarea
                    value={formData.careerObjective}
                    onChange={(e) => handleInputChange('careerObjective', e.target.value)}
                    placeholder="Write your objective here"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    rows={4}
                  />
                </FormControl>

                {/* Achievement */}
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontSize="sm">Achievement (Related to Career and Study)</FormLabel>
                  <Textarea
                    value={formData.achievement}
                    onChange={(e) => handleInputChange('achievement', e.target.value)}
                    placeholder="Write your achievement here"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    rows={4}
                  />
                </FormControl>

                {/* Message to Recruiter */}
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontSize="sm">Message to Recruiter (If Any)</FormLabel>
                  <Textarea
                    value={formData.messageToRecruiter}
                    onChange={(e) => handleInputChange('messageToRecruiter', e.target.value)}
                    placeholder="Write your message here"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    rows={4}
                  />
                </FormControl>

                {/* Submit Button */}
                <Box textAlign="center" mt={6}>
                  <Button
                    type="submit"
                    bg="#4C63B6"
                    color="white"
                    px={12}
                    py={3}
                    borderRadius="md"
                    _hover={{ bg: '#3A4D8F' }}
                    fontWeight="bold"
                    isDisabled={
                      duplicateStatus.email.isDuplicate || 
                      duplicateStatus.mobile.isDuplicate ||
                      duplicateStatus.email.isChecking ||
                      duplicateStatus.mobile.isChecking
                    }
                    isLoading={duplicateStatus.email.isChecking || duplicateStatus.mobile.isChecking}
                    loadingText="Checking..."
                  >
                    Next
                  </Button>
                  {(duplicateStatus.email.isDuplicate || duplicateStatus.mobile.isDuplicate) && (
                    <Text color="red.500" fontSize="sm" mt={2}>
                      Please use different email/mobile to continue
                    </Text>
                  )}
                </Box>
              </VStack>
            </form>
          </VStack>
          ) : (
            <ApplicationFormPart2 
              formData={formData}
              setFormData={setFormData}
              onPrevious={handlePrevious}
              onSubmit={handleFinalSubmit}
              jobCircularId={jobCircularId}
              jobInfo={jobInfo}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ApplicationForm;
